import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserManagement } from '../../../../../hooks/user/useUserManagement'

import { ListRow } from '../../../../listItem/listRow/ListRow'
import { UserCard } from '../userCard/UserCard'
import { UserCardActions } from '../userCardActions/UserCardActions'
import { UserStatusBadge } from '../userStatusBadge/UserStatusBadge'
import { UserRolesInput } from './userRoleInput/UserRolesInput'
import { ListingDetails } from '../../../../listing/ListingDetails'
import { Tabs } from '../../../../tabs/Tabs'

import { useApp } from '../../../../../contexts/AppContext'
import { CONFIG } from '../../../../../config'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import {
  ProfileMetadata,
  UserRoleFilter,
} from '../../../../../types/user/userProfile'
import { DisplayType } from '../../../../../types/listing'
import { ActionType } from '../../../../../constants/actionTypes'
import { RoleType } from '../../../../../utils/jwtUtils'
import {
  formatUserJoinedAt,
  getUserDisplayName,
} from '../../../../../utils/users/user'
import { initials } from '../../../../../utils/profile/profile'

type UsersListProps = {
  roleFilter: UserRoleFilter
  onRoleFilterChange: (filter: UserRoleFilter) => void
  onUsersChange?: () => void
}

export const UsersList = ({
  roleFilter,
  onRoleFilterChange,
  onUsersChange,
}: UsersListProps) => {
  const { showBanner } = useApp()
  const { t, i18n } = useTranslation('users')
  const [refreshTrigger, setRefreshTrigger] = useState(false)

  const handleRefresh = useCallback(() => {
    setRefreshTrigger(current => !current)
    onUsersChange?.()
  }, [onUsersChange])

  const { handleAction, actionLoading } = useUserManagement({
    onRefresh: handleRefresh,
  })

  const roleFilterOptions = useMemo(
    () => [
      {
        value: 'ALL',
        label: t('users.roles.all'),
      },
      ...Object.values(RoleType).map(role => ({
        value: role,
        label: t(`users.roles.${role.toLowerCase()}`),
      })),
    ],
    [t]
  )

  const tableColumns = [
    { key: 'user', label: t('users.list.user') },
    { key: 'roles', label: t('users.list.roles') },
    { key: 'joined', label: t('users.list.joined') },
    { key: 'status', label: t('users.list.status') },
    { key: 'actions', label: t('users.actions.more') },
  ]

  const renderUser = useCallback(
    (profile: ProfileMetadata, viewMode: DisplayType) => {
      const joined = formatUserJoinedAt(profile.joinedAt, i18n.language)
      const displayName = getUserDisplayName(profile)
      const hasName = displayName !== profile.email

      if (viewMode === DisplayType.LIST) {
        return (
          <ListRow
            key={profile.id}
            cells={[
              {
                key: 'user',
                content: (
                  <button
                    type="button"
                    className="users-list-identity"
                    onClick={() => void handleAction(profile, ActionType.VIEW)}
                  >
                    <div className="ph-avatar sm">{initials(displayName)}</div>
                    <div className="users-list-contact">
                      <div className="users-list-name" title={displayName}>
                        {displayName}
                      </div>
                      {hasName && (
                        <div
                          className="users-list-email mono tiny muted"
                          title={profile.email}
                        >
                          {profile.email}
                        </div>
                      )}
                    </div>
                  </button>
                ),
              },
              {
                key: 'roles',
                content: (
                  <UserRolesInput
                    user={profile}
                    loading={actionLoading}
                    onRolesChange={roles =>
                      void handleAction(profile, ActionType.UPDATE, roles)
                    }
                  />
                ),
              },
              {
                key: 'joined',
                className: 'mono text-nowrap',
                content: joined,
              },
              {
                key: 'status',
                content: (
                  <UserStatusBadge
                    status={profile.status}
                    locked={profile.locked}
                  />
                ),
              },
            ]}
            actions={
              <UserCardActions
                user={profile}
                onAction={handleAction}
                actionLoading={actionLoading}
              />
            }
          />
        )
      }

      return (
        <UserCard
          key={profile.id}
          user={profile}
          roles={
            <UserRolesInput
              user={profile}
              loading={actionLoading}
              onRolesChange={roles =>
                void handleAction(profile, ActionType.UPDATE, roles)
              }
            />
          }
          onAction={handleAction}
          actionLoading={actionLoading}
        />
      )
    },
    [actionLoading, handleAction, i18n.language]
  )

  const handleListError = useCallback(
    (message: string) => {
      showBanner({
        kind: BannerKind.ERR,
        msg: message,
      })
    },
    [showBanner]
  )

  return (
    <ListingDetails<ProfileMetadata>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/profiles`}
      initialViewMode={DisplayType.LIST}
      displaySearch
      additionalParams={roleFilter === 'ALL' ? {} : { roleType: roleFilter }}
      searchPlaceholder={t('users.filters.search-placeholder')}
      filter={
        <Tabs
          tab={roleFilter}
          tabs={roleFilterOptions}
          onChange={role => onRoleFilterChange(role as UserRoleFilter)}
        />
      }
      refreshTrigger={refreshTrigger}
      tableColumns={tableColumns}
      gridItemClassName="col-12 col-xxl-6"
      onError={handleListError}
      renderItem={renderUser}
    />
  )
}
